import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  Clock,
  Flame,
  GitBranch,
  Layers,
  Target,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime, dayOffsetFromToday } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { myTasksResponse, myGamificationStats } from '../../lib/types'

/**
 * /app/focus-board — the **Focus Board**. Single-page "what should I
 * work on right now" surface for any signed-in user. Inspired by
 * Sunsama / Things 3:
 *
 *   - top: page title + today's date + actionable count
 *   - left column: today's tasks list (overdue, due today, due tomorrow)
 *   - right column: focus count + points
 *
 * Reads only /me/tasks + /gamification/me — no new backend.
 *
 * Note: the component is still called `MyDayPage` and the file is still
 * `MyDayPage.jsx` — only the URL and the user-facing label moved to
 * "Focus Board". The legacy `/app/my-day` URL still works via a
 * redirect in `App.jsx` so old bookmarks don't break.
 */
export function MyDayPage() {
  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: async () => {
      const res = await api.get('/me/tasks')
      return myTasksResponse.parse(res.data)
    },
  })

  const statsQuery = useQuery({
    queryKey: ['gamification', 'me'],
    queryFn: async () => {
      const res = await api.get('/gamification/me')
      return myGamificationStats.parse(res.data)
    },
  })

  const buckets = useMemo(() => {
    const all = tasksQuery.data?.data ?? []
    const overdue = []
    const dueToday = []
    const dueTomorrow = []
    const upcoming = []
    const nowMs = Date.now()
    for (const t of all) {
      // Skip already-completed tasks from the action lists. They stay
      // accessible via My Tasks if the user wants to revisit them.
      if (t.completedAt) continue
      // No due date → not actionable on this time-bucketed board; skip.
      if (!t.dueDate) continue
      // Calendar-day offset in the tenant's display timezone (0 = today,
      // <0 = past, 1 = tomorrow). Used for date-only due dates and for the
      // Due Today / Due Tomorrow buckets. Bucketing by calendar day (not a raw
      // browser-zone instant) keeps a date-only due date (stored at midnight)
      // from shifting across the UTC boundary.
      const offset = dayOffsetFromToday(t.dueDate)
      if (offset == null) continue
      // Overdue is TIME-AWARE, matching the backend (utils/task-overdue.js) and
      // the Dashboard counts: a TIMED due date is overdue the moment its instant
      // passes — even if that instant is earlier TODAY (e.g. due 11:00, now
      // 12:45). A date-only due date is overdue only once the whole day has
      // passed (offset < 0). Without this, a timed task due earlier today wrongly
      // stayed under "Due Today" here while the Dashboard already counted it
      // overdue, so the two surfaces disagreed.
      const pastDue = t.dueDateHasTime
        ? new Date(t.dueDate).getTime() < nowMs
        : offset < 0
      if (pastDue) overdue.push(t)
      else if (offset === 0) dueToday.push(t)
      else if (offset === 1) dueTomorrow.push(t)
      else upcoming.push(t)
    }
    // Order each bucket by priority desc then due asc.
    const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3, null: 4 }
    const sort = (list) =>
      list.sort((a, b) => {
        const pa = priorityOrder[a.priority ?? 'null']
        const pb = priorityOrder[b.priority ?? 'null']
        if (pa !== pb) return pa - pb
        if (a.dueDate && b.dueDate)
          return new Date(a.dueDate) - new Date(b.dueDate)
        return 0
      })
    return {
      overdue: sort(overdue),
      dueToday: sort(dueToday),
      dueTomorrow: sort(dueTomorrow),
      upcoming: sort(upcoming).slice(0, 5),
    }
  }, [tasksQuery.data])

  const stats = statsQuery.data
  const totalActionable =
    buckets.overdue.length + buckets.dueToday.length + buckets.dueTomorrow.length

  return (
    <div>
      <Breadcrumb current="Focus Board" homePath="/app" homeLabel="Dashboard" />

      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Target
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Focus Board
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            {formatDateTime(new Date())}{' '}
            ·{' '}
            {totalActionable === 0
              ? 'All clear. Pick something to do.'
              : `${totalActionable} task${totalActionable === 1 ? '' : 's'} need your attention.`}
          </p>
        </div>
      </div>

      {tasksQuery.isLoading ? (
        <div className="text-[color:var(--color-ink-muted)]">Loading…</div>
      ) : tasksQuery.isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(tasksQuery.error).message}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          {/* Left column — task buckets */}
          <div className="space-y-4">
            {buckets.overdue.length > 0 && (
              <Bucket
                title="Overdue"
                tasks={buckets.overdue}
                icon={AlertCircle}
                accent="#ef4444"
              />
            )}
            <Bucket
              title="Due Today"
              tasks={buckets.dueToday}
              icon={Target}
              accent="#6366f1"
              emptyMessage="Nothing due today — get ahead on tomorrow."
            />
            <Bucket
              title="Due Tomorrow"
              tasks={buckets.dueTomorrow}
              icon={Clock}
              accent="#f59e0b"
              emptyMessage="Tomorrow is open."
            />
            {buckets.upcoming.length > 0 && (
              <Bucket
                title="This Week"
                tasks={buckets.upcoming}
                icon={ArrowRight}
                accent="#0ea5e9"
              />
            )}
          </div>

          {/* Right column — progress sidebar */}
          <div className="space-y-4">
            <ProgressCard
              dueToday={buckets.dueToday.length}
              overdue={buckets.overdue.length}
            />
            <PointsCard stats={stats} loading={statsQuery.isLoading} />
          </div>
        </div>
      )}
    </div>
  )
}

// Desktop (sm+) table grid — the column header row and every TaskRow share it
// so the columns line up vertically. On MOBILE (< sm) the header is hidden and
// each row STACKS instead (name on its own full-width line, priority + due date
// underneath), because two fixed 120px columns + padding crush the task name to
// a few characters at phone width. Both header and row apply this identically,
// so alignment holds at every breakpoint.
const ROW_GRID_SM =
  'sm:grid sm:grid-cols-[minmax(0,1fr)_120px_120px] sm:items-center sm:gap-3'

function Bucket({ title, tasks, icon: Icon, accent, emptyMessage }) {
  return (
    <div className="rounded-[12px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
      {/* Tinted title bar — accent-coloured icon, bucket name, count. */}
      <div
        className="flex items-center gap-3 px-5 py-3 border-b border-[color:var(--color-rule)]"
        style={{ backgroundColor: `${accent}0c` }}
      >
        <span
          className="h-8 w-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Icon size={16} />
        </span>
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 flex-1">
          {title}
        </h3>
        <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums">
          {tasks.length}
        </span>
      </div>
      {tasks.length === 0 ? (
        <div className="px-5 py-6 text-sm text-[color:var(--color-ink-muted)] text-center">
          {emptyMessage ?? 'Nothing here.'}
        </div>
      ) : (
        <>
          {/* Column header row — only rendered when there ARE rows
           *  underneath. Same grid as the data so columns line up. */}
          <div
            className={
              // Hidden on mobile — the stacked rows below don't need a
              // column header, and forcing one into phone width is what
              // squeezed the data. Shown as the aligned grid on sm+.
              'hidden px-5 py-2 text-left text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] ' +
              ROW_GRID_SM
            }
          >
            <span>Task</span>
            <span>Priority</span>
            <span>Due Date</span>
          </div>
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} />
          ))}
        </>
      )}
    </div>
  )
}

// Same shape and tokens as `PRIORITY_META` on SpacePage so the priority
// label and colour stay in sync across pages. The Flag icon used on
// SpacePage is intentionally not rendered here — these rows live under
// a "Priority" column header and the icon would shift the data text
// out of alignment with the header.
const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--color-priority-urgent)' },
  high: { label: 'High', color: 'var(--color-priority-high)' },
  normal: { label: 'Medium', color: 'var(--color-priority-normal)' },
  low: { label: 'Low', color: 'var(--color-priority-low)' },
}

function TaskRow({ task }) {
  const meta = PRIORITY_META[task.priority ?? 'normal']
  // Overdue = still-open task whose due date is in the past. Uses the same
  // calendar-day, tenant-timezone offset the page buckets by, and treats a
  // completed task (completedAt set = closed-category status) as never
  // overdue — so a badged row always agrees with the Overdue count/bucket
  // (DF_017 / bug 13332).
  const dueOffset = task.dueDate ? dayOffsetFromToday(task.dueDate) : null
  const isOverdue = !task.completedAt && dueOffset != null && dueOffset < 0
  // Only build the deep-link if BOTH ids are present. A previous version
  // substituted `_` for a missing spaceId, which produced a route that
  // doesn't exist (`/app/spaces/_/lists/…`) and 404'd. Fall back to the
  // user's task feed instead — the task is still discoverable there.
  const target =
    task.listId && task.spaceId
      ? `/app/spaces/${task.spaceId}/lists/${task.listId}`
      : '/app/focus-board'
  return (
    <Link
      to={target}
      className={
        // Mobile: a stacked flex column (name line, then priority + due date
        // on their own line) so the task name gets the full width instead of a
        // ~70px sliver. sm+: the aligned three-column grid, unchanged.
        'flex flex-col gap-1.5 px-4 sm:px-5 py-3 border-b border-[color:var(--color-rule)] last:border-b-0 hover:bg-[color:var(--color-card-hover)] transition-colors text-left ' +
        ROW_GRID_SM
      }
    >
      {/* Task column — leading priority colour dot, the task name (with a
       *  "Subtask" tag when it's nested under a parent), and a muted
       *  sub-line naming the Space (and List) the task lives in. The Focus
       *  Board aggregates across every Space, so without the Space line a
       *  bare task name is ambiguous — and without the tag a subtask is
       *  indistinguishable from a top-level task. */}
      <div className="flex items-start gap-3 min-w-0">
        <span
          className="h-2 w-2 rounded-full shrink-0 mt-1.5"
          style={{ backgroundColor: meta.color }}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate text-sm text-[color:var(--color-ink)]">
              {task.name}
            </span>
            {isOverdue ? (
              <span className="shrink-0 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[color:var(--color-priority-urgent)]/10 text-[color:var(--color-priority-urgent)]">
                Overdue
              </span>
            ) : null}
            {task.parentTaskId ? (
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-[color:var(--color-rule)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[color:var(--color-ink-muted)]">
                <GitBranch size={9} aria-hidden="true" />
                Subtask
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-[color:var(--color-ink-muted)] min-w-0">
            <Layers size={11} className="shrink-0" aria-hidden="true" />
            <span className="truncate">
              {task.spaceName}
              {task.listName ? ` · ${task.listName}` : ''}
            </span>
          </div>
        </div>
      </div>
      {/* Priority + due date. On mobile this wrapper is a flex row that sits on
       *  its own line beneath the task name; on sm+ `sm:contents` dissolves it
       *  so both children become their own grid columns and line up under the
       *  "Priority" / "Due Date" headers. */}
      <div className="flex items-center gap-3 min-w-0 sm:contents">
        {/* Priority — coloured title-case label. No leading icon: removing it
         *  is what gets the data to align with the header above. `capitalize`
         *  renders the raw "urgent"/"high"/… as "Urgent"/"High"/…. */}
        <span
          className="text-sm capitalize shrink-0"
          style={task.priority ? { color: meta.color } : undefined}
        >
          {task.priority ?? (
            <span className="text-[color:var(--color-ink-subtle)]">—</span>
          )}
        </span>
        {/* Due date — muted body text. Canonical DD/MM/YYYY HH:MM AM/PM. */}
        <span className="text-sm text-[color:var(--color-ink-muted)] tabular-nums">
          {task.dueDate ? formatDateTime(task.dueDate) : '—'}
        </span>
      </div>
    </Link>
  )
}

function ProgressCard({ dueToday, overdue }) {
  const total = dueToday + overdue
  return (
    <div className="rounded-[12px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-3 flex items-center gap-2">
        <Target size={16} className="text-[color:var(--color-accent-strong)]" />
        Focus
      </h3>
      <div className="font-display text-4xl font-semibold text-[color:var(--color-ink-strong)] leading-none">
        {total}
      </div>
      <div className="text-xs text-[color:var(--color-ink-muted)] mt-1.5">
        tasks needing action today
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[color:var(--color-ink-muted)]">
            <AlertCircle size={12} className="text-red-500" />
            Overdue
          </span>
          <span className="font-medium tabular-nums">{overdue}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[color:var(--color-ink-muted)]">
            <Clock size={12} className="text-indigo-500" />
            Due Today
          </span>
          <span className="font-medium tabular-nums">{dueToday}</span>
        </div>
      </div>
    </div>
  )
}

function PointsCard({ stats, loading }) {
  return (
    <div className="rounded-[12px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 mb-3 flex items-center gap-2">
        <Flame size={16} className="text-amber-500" />
        Your Points
      </h3>
      {loading ? (
        <div className="text-sm text-[color:var(--color-ink-muted)]">Loading…</div>
      ) : (
        <>
          <div className="font-display text-4xl font-semibold text-[color:var(--color-ink-strong)] leading-none">
            {stats?.total ?? 0}
          </div>
          <div className="text-xs text-[color:var(--color-ink-muted)] mt-1.5">
            {stats?.badges?.length ?? 0} badge
            {(stats?.badges?.length ?? 0) === 1 ? '' : 's'} earned
          </div>
          <Link
            to="/app/leaderboard"
            className="inline-flex items-center gap-1 text-xs text-[color:var(--color-accent)] hover:underline mt-3"
          >
            View leaderboard <ArrowRight size={12} />
          </Link>
        </>
      )}
    </div>
  )
}

